import { type ReactNode } from 'react';
import { cn } from '../lib/cn';

export interface StatCardProps {
  label: string;
  value: ReactNode;
  /** Small caption under the value, e.g. "of 512 members". */
  sub?: ReactNode;
  icon?: ReactNode;
  /** Highlights the metric the gym should act on (uses the lime accent surface). */
  emphasis?: boolean;
  /**
   * Optional trend pill, e.g. "+12%". When `good` is set, the pill is coloured by whether the
   * movement is favourable (green) or not (red): `good: true` means "up is good" (revenue,
   * sign-ups), `good: false` means "up is bad" (lapsed, refunds). Omit `good` for a neutral pill.
   */
  trend?: { value: string; direction: 'up' | 'down' | 'flat'; good?: boolean };
  /**
   * When set, the whole card becomes a button that drills into the detail behind the number
   * (who checked in, which payments, …). Adds hover/focus affordance and a "view" hint.
   */
  onClick?: () => void;
  /** Verb shown in the drill-in hint, e.g. "View check-ins". Defaults to "View details". */
  actionLabel?: string;
  className?: string;
}

/** Whether a trend movement is favourable: true = green, false = red, null = neutral (no `good`, or flat). */
function trendFavourable(trend: NonNullable<StatCardProps['trend']>): boolean | null {
  if (trend.good === undefined || trend.direction === 'flat') return null;
  return trend.good ? trend.direction === 'up' : trend.direction === 'down';
}

export function StatCard({
  label,
  value,
  sub,
  icon,
  emphasis = false,
  trend,
  onClick,
  actionLabel = 'View details',
  className,
}: StatCardProps) {
  const interactive = !!onClick;
  const Root = interactive ? 'button' : 'div';
  return (
    <Root
      {...(interactive ? { type: 'button' as const, onClick } : {})}
      className={cn(
        'group flex flex-col gap-5 rounded-card p-5 text-left shadow-card',
        emphasis ? 'bg-accent text-accent-ink' : 'bg-card text-ink',
        interactive &&
          'cursor-pointer transition hover:-translate-y-0.5 hover:shadow-card-hover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ink/25',
        className,
      )}
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2.5">
          {icon && (
            <span
              className={cn(
                'inline-flex size-9 items-center justify-center rounded-control',
                emphasis ? 'bg-accent-ink/10 text-accent-ink' : 'bg-surface text-ink-soft',
              )}
            >
              {icon}
            </span>
          )}
          <span className="text-sm font-medium">{label}</span>
        </div>
        {trend && (
          <span
            className={cn(
              'rounded-pill px-2 py-0.5 text-xs font-medium tabular',
              emphasis
                ? 'bg-accent-ink/10'
                : trendFavourable(trend) === true
                  ? 'bg-ok-soft text-ok-ink'
                  : trendFavourable(trend) === false
                    ? 'bg-bad-soft text-bad-ink'
                    : 'bg-surface text-ink-soft',
            )}
          >
            {trend.direction === 'up' ? '↑' : trend.direction === 'down' ? '↓' : '→'} {trend.value}
          </span>
        )}
      </div>
      <div>
        <div className="font-display text-metric tabular">{value}</div>
        {sub && <div className={cn('mt-1 text-sm', emphasis ? 'text-accent-ink/70' : 'text-muted')}>{sub}</div>}
        {interactive && (
          <div
            className={cn(
              'mt-1 inline-flex items-center gap-1 text-xs font-medium opacity-0 transition-opacity group-hover:opacity-100 group-focus-visible:opacity-100',
              emphasis ? 'text-accent-ink' : 'text-ink-soft',
            )}
          >
            {actionLabel} <span aria-hidden>→</span>
          </div>
        )}
      </div>
    </Root>
  );
}
